// @ts-check
/**
 * Strom „Všechny obory a kódy“ v panelu Obory (sekce 13 R-8, přechodová tabulka; FR-13-32 až
 * FR-13-34, BR-13-06, BR-13-09; upřesnění 2 a 10 karty B2). Knihovna modulu `list.js`, který ji
 * spouští nad svým kořenem a po každé úspěšné výměně seznamu (i po Zpět) volá `sync(url)`.
 *
 * Kořen nese odkaz `a[data-tree-toggle]` (bez JS vede na stránku `/sectors`) a prázdný skrytý slot
 * `[data-sector-tree]`; bez kteréhokoli z nich knihovna nic neregistruje a `sync` je bez účinku
 * (stránka zadavatele, samostatná `/sectors`).
 *
 * Stavy: sbalený (slot prázdný) → prostý klik (`lib/click.js`) → načítá: `aria-expanded="true"`,
 * `aria-busy="true"`, fragment `href` odkazu (`lib/fragment.js`, `AbortController`). Načítá →
 * 200 `text/html` s `[data-sector-tree]` → rozbalený: `swap` (do slotu jdou jen POTOMCI obalu
 * fragmentu), slot a pole hledání `[data-tree-search]` bez `hidden`, `aria-busy` pryč, `sync`
 * podle zobrazené adresy, fokus na pole hledání. Načítá → druhý klik → sbalený (`abort`,
 * `aria-expanded="false"`; zrušení není selhání). Načítá → selhání (síť, stav ≠ 200, Content-Type,
 * fragment bez obalu) → `navigate(href)` celou stránkou, nic se nepřepíše. Rozbalený ⇄ skrytý
 * klikem: jen `hidden` slotu a `aria-expanded`, bez požadavku — stav rozbalení žije jen v DOM,
 * v adrese je výhradně výběr (BR-13-06).
 *
 * `sync(url)`: zaškrtávátka `input[type=checkbox][name=cpv]` ve slotu = kódy `cpv` adresy (adresa
 * je stav; nadbytečný kód se odškrtne, BR-13-02) a z formuláře, kterému patří (`form=`), se
 * odstraní skrytá pole `cpv` kódů, které ve slotu zaškrtávátko mají — jediný nositel kódu
 * (upřesnění 2): kód ve stromu nese zaškrtávátko, kód mimo strom skryté pole, které server kreslí
 * v každém fragmentu formuláře podle adresy. Bez rozbaleného stromu `sync` nic nemění. Druhý
 * parametr = fragment seznamu: odkaz stromu stojí mimo oblasti výměny, proto `sync` převezme jeho
 * `href` (místo a výběr nové adresy). Změnilo-li se v adrese místo (`region`, `district`,
 * `municipality`), strom patří starému místu → sbalený (slot prázdný, `hidden`, bez požadavku;
 * rozhodnutí architekta na revizi zastávky B2, nový řádek tabulky R-8) a kódy nesou skrytá pole.
 *
 * Hledání: `input` v poli stromu → ustálení `lib/settle.js` (300 ms) → dotaz složený na malá
 * písmena bez diakritiky (`normalize('NFD')` bez `\p{M}`; `data-search` šablona skládá týmž
 * způsobem). Řádek `label[data-search]` je vidět se shodou svou nebo názvu svého oboru, obor
 * (`details[data-search]`) bez shody názvu i řádků se skryje, se shodou otevře; prázdný dotaz =
 * vše viditelné a `open` beze změny. Změnu zaškrtávátka kódu obsluhuje `list.js` (form-associated
 * prvek formuláře filtrů); strom při výměně seznamu zůstává.
 *
 * Žádné texty UI (věty nese fragment), žádný `innerHTML`, nic mimo kořen; `signal` ruší posluchače,
 * ustálení i rozpracovaný požadavek. Závislosti jdou vstříknout (jsdom testy).
 */

import { plainClick } from './click.js';
import { fetchFragment, swap } from './fragment.js';
import { settle } from './settle.js';

/** Slot stromu v panelu i obal fragmentu `/sectors`. */
const SLOT = '[data-sector-tree]';
/** Odkaz „Všechny obory a kódy“. */
const TOGGLE = 'a[data-tree-toggle][href]';
/** Pole hledání ve fragmentu. */
const SEARCH = 'input[data-tree-search]';
/** Zaškrtávátko kódu. */
const CODE = 'input[type="checkbox"][name="cpv"]';
/** Parametry místa v adrese (výpis); na stránkách míst nese místo cesta. */
const PLACE = ['region', 'district', 'municipality'];
/** Ustálení hledání v ms (ui-checklist bod 14). */
const SETTLE_MS = 300;

/**
 * @typedef {object} TreeDeps
 * @property {import('./fragment.js').Fetcher} [fetch]
 * @property {(url: string) => void} [navigate] přechod celou stránkou (výchozí `location.assign`)
 * @property {import('./settle.js').Timers} [timers] časovače ustálení hledání (testy)
 * @property {AbortSignal} [signal] zrušení posluchačů, ustálení a požadavku
 * @property {() => string} [here] zobrazená adresa (cesta + dotaz)
 */

/**
 * Dotaz i text hledání: malá písmena bez diakritiky.
 * @param {string} text
 */
function fold(text) {
  return text.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
}

/**
 * Spustí strom nad kořenem modulu seznamu.
 * @param {HTMLElement} root
 * @param {TreeDeps} [deps]
 * @returns {{ sync: (url: string, fragment?: Document) => void }}
 */
export function initSectorTree(root, deps = {}) {
  const doc = root.ownerDocument;
  const win = /** @type {Window & typeof globalThis} */ (doc.defaultView ?? globalThis);
  const slot = /** @type {HTMLElement | null} */ (root.querySelector(SLOT));
  const toggle = root.querySelector(TOGGLE);
  if (slot === null || toggle === null) return { sync() {} };

  const doFetch = deps.fetch ?? /** @type {import('./fragment.js').Fetcher} */ (win.fetch);
  const navigate = deps.navigate ?? ((url) => win.location.assign(url));
  const here = deps.here ?? (() => win.location.pathname + win.location.search);
  const listen = { signal: deps.signal };

  /** @type {'collapsed' | 'loading' | 'open' | 'hidden'} */
  let state = 'collapsed';
  /** @type {AbortController | null} */
  let inflight = null;

  /** @param {boolean} expanded */
  function expand(expanded) {
    toggle?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  /** Místo adresy (parametry region, district, municipality); na stránkách míst je v cestě. @param {string} url */
  function placeOf(url) {
    const params = new win.URL(url, win.location.href).searchParams;
    return PLACE.map((name) => params.getAll(name).join(',')).join('|');
  }
  /** Místo adresy, ke které strom patří (poslední sync). */
  let place = placeOf(here());

  /**
   * @param {string} url zobrazená adresa po výměně seznamu (nebo Zpět)
   * @param {Document} [fragment] fragment seznamu — nese odkaz stromu nové adresy
   */
  function sync(url, fragment) {
    // Odkaz stojí mimo oblasti výměny seznamu: href (místo, výběr, původ) z fragmentu.
    const href = fragment?.querySelector(TOGGLE)?.getAttribute('href');
    if (href) toggle?.setAttribute('href', href);
    const next = placeOf(url);
    const moved = next !== place;
    place = next;
    // Změna místa (rozhodnutí architekta, revize zastávky B2): strom patří starému místu →
    // sbalit PŘED odstraňováním skrytých cpv, kódy pak nesou skrytá pole formuláře.
    if (moved && state !== 'collapsed') collapse();
    if (state === 'collapsed' || state === 'loading' || slot === null) return;
    const codes = new Set(new win.URL(url, win.location.href).searchParams.getAll('cpv'));
    /** @type {Set<string>} */
    const inTree = new Set();
    /** @type {HTMLFormElement | null} */
    let owner = null;
    for (const el of slot.querySelectorAll(CODE)) {
      const box = /** @type {HTMLInputElement} */ (el);
      box.checked = codes.has(box.value);
      inTree.add(box.value);
      owner ??= box.form;
    }
    if (owner === null) return;
    for (const el of Array.from(owner.elements)) {
      if (
        el instanceof win.HTMLInputElement &&
        el.type === 'hidden' &&
        el.name === 'cpv' &&
        inTree.has(el.value)
      ) {
        el.remove();
      }
    }
  }

  /** @param {string} href */
  async function load(href) {
    const ctrl = new win.AbortController();
    inflight = ctrl;
    state = 'loading';
    expand(true);
    toggle?.setAttribute('aria-busy', 'true');
    /** @type {Document} */
    let fragment;
    try {
      fragment = (
        await fetchFragment(href, { signal: ctrl.signal, fetch: doFetch, base: win.location.href })
      ).doc;
    } catch {
      if (!ctrl.signal.aborted) navigate(href);
      return;
    }
    if (ctrl.signal.aborted) return;
    inflight = null;
    try {
      if (fragment.querySelector(SLOT) === null) throw new Error(`ve fragmentu chybí ${SLOT}`);
      swap(root, fragment, SLOT);
    } catch {
      navigate(href);
      return;
    }
    state = 'open';
    if (slot !== null) slot.hidden = false;
    toggle?.removeAttribute('aria-busy');
    const field = /** @type {HTMLInputElement | null} */ (slot?.querySelector(SEARCH) ?? null);
    if (field !== null) field.hidden = false;
    sync(here());
    field?.focus();
  }

  /** Sbalený stav: zrušit rozpracovaný požadavek, slot prázdný a skrytý (bez požadavku). */
  function collapse() {
    inflight?.abort();
    inflight = null;
    state = 'collapsed';
    expand(false);
    toggle?.removeAttribute('aria-busy');
    slot?.replaceChildren();
    if (slot !== null) slot.hidden = true;
  }

  root.addEventListener(
    'click',
    (event) => {
      if (!(event instanceof win.MouseEvent) || !plainClick(event)) return;
      const target = event.target;
      if (!(target instanceof win.Element)) return;
      const link = target.closest(TOGGLE);
      if (link === null || !root.contains(link)) return;
      const href = link.getAttribute('href');
      if (href === null || slot === null) return;
      event.preventDefault();
      if (state === 'collapsed') {
        void load(href);
      } else if (state === 'loading') {
        collapse();
      } else {
        const show = state === 'hidden';
        state = show ? 'open' : 'hidden';
        slot.hidden = !show;
        expand(show);
      }
    },
    listen,
  );

  /** @param {string} query */
  function filter(query) {
    if (slot === null) return;
    const q = fold(query.trim());
    // Shoda jen v názvu oboru obor ukáže, otevře a nechá viditelné všechny jeho kódy (rozhodnutí
    // architekta na zastávce řezu B2, 26. 10. 2026 — tabulka R-8 případ neřešila).
    for (const el of slot.querySelectorAll('details[data-search]')) {
      const node = /** @type {HTMLDetailsElement} */ (el);
      const named = q === '' || (node.dataset.search ?? '').includes(q);
      let rowHit = false;
      for (const r of node.querySelectorAll('label[data-search]')) {
        const line = /** @type {HTMLElement} */ (r);
        const hit = q !== '' && (line.dataset.search ?? '').includes(q);
        rowHit ||= hit;
        line.hidden = !(named || hit);
      }
      node.hidden = !(named || rowHit);
      if (q !== '' && !node.hidden) node.open = true;
    }
  }

  const search = settle(filter, SETTLE_MS, deps.timers ?? {});
  deps.signal?.addEventListener('abort', () => {
    search.cancel();
    inflight?.abort();
  });
  root.addEventListener(
    'input',
    (event) => {
      const target = event.target;
      if (!(target instanceof win.HTMLInputElement) || !target.matches(SEARCH)) return;
      if (!slot.contains(target)) return;
      search(target.value);
    },
    listen,
  );

  return { sync };
}
